import React from "react";
import { SpotShape } from "../../../../../../types";
import { ColorPickerInput } from "../../../../../ui/ColorPickerInput";
import { EditorFieldRow } from "../../../../../ui/EditorField";
import { PanelSection } from "../../../../../ui/PanelShell";
import { useMapCanvas } from "../../../../contexts/MapCanvasContext";
import { useMapEditorActions } from "../../../../contexts/MapEditorContext";
import { ShapeSectionOptions } from "../ShapePanel";

export const CommonColorPanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape } = useMapEditorActions();
  const { shapesRef } = useMapCanvas();
  const shape = shapes[0];
  const isSpot = shape.type === "spot";

  const handleChangeColor = (value: string) => {
    shapes.forEach((shape) => {
      if (shape.type === "spot") {
        const spot = shape as SpotShape;
        const updates: Partial<SpotShape> = {
          color: value,
          colorSource: "manual"
        };
        // Propagate color to companion shapes (except spots)
        if (spot.companions?.length) {
          updates.companions = spot.companions.map((c) =>
            c.type === "spot" ? c : { ...c, color: value }
          );
        }
        updateShape(shape.id, updates);
      } else {
        updateShape(shape.id, { color: value });
      }
    });
  };

  const handleResetColor = () => {
    shapes.forEach((shape) => {
      const updates: Partial<SpotShape> = {
        color: undefined,
        colorSource: undefined
      };
      if (shape.type === "spot") {
        const spot = shape as SpotShape;
        if (spot.companions?.length) {
          updates.companions = spot.companions.map((c) =>
            c.type === "spot" ? c : { ...c, color: undefined }
          );
        }
      }
      updateShape(shape.id, updates);
    });
  };

  const hasManualColor =
    isSpot &&
    ((shape as SpotShape).colorSource === "manual" ||
      (!!(shape as SpotShape).color && !(shape as SpotShape).colorSource));

  // Build palette from all shapes' colors. Uses shapesRef to avoid
  // subscribing to the shapes context (which would re-render on every
  // shape change). The palette updates when this component re-renders
  // for other reasons (e.g. selection change).
  const paletteColors: string[] = React.useMemo(() => {
    return shapesRef.current.reduce((colors, shape) => {
      if (shape.color && !colors.includes(shape.color)) {
        colors.push(shape.color);
      }

      return colors;
    }, [] as string[]);
  }, [shapes]);

  return (
    <PanelSection label="Color">
      <div className="flex flex-col gap-2">
        <EditorFieldRow label="Color">
          <ColorPickerInput
            value={shape.color || ""}
            onChange={handleChangeColor}
            paletteColors={paletteColors}
          />
        </EditorFieldRow>
        {isSpot && hasManualColor && (
          <button
            type="button"
            className="self-start text-blue-600 text-xs hover:text-blue-800"
            onClick={handleResetColor}
          >
            Reset to auto
          </button>
        )}
      </div>
    </PanelSection>
  );
};
